import React from 'react';
import { Comic } from '../types/Comic';
import './ComicTable.css';

interface ComicTableProps {
  comics: Comic[];
  onEdit: (comic: Comic) => void;
  onDelete: (email: string) => void;
}

const SPOT_RATE_LABELS: Record<string, string> = {
  standard: '$25',
  senior: '$40',
};

const formatSpotRate = (comic: Comic): string => {
  const rate = comic.spot_rate ?? 'standard';
  if (rate === 'custom') return `$${comic.custom_spot_rate}`;
  return SPOT_RATE_LABELS[rate] ?? '$25';
};

const ComicTable: React.FC<ComicTableProps> = ({ comics, onEdit, onDelete }) => {
  return (
    <div className="comic-table-container">
      <table className="comic-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Instagram</th>
            <th>Gender</th>
            <th>Friend</th>
            <th>Cellar Comic</th>
            <th>Can Host</th>
            <th>Do Not Book</th>
            <th>Spot Rate</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {comics.length === 0 ? (
            <tr>
              <td colSpan={10} className="no-data">
                No comics found. Add your first comic to get started!
              </td>
            </tr>
          ) : (
            comics.map((comic) => (
              <tr key={comic.email}>
                <td className="name-cell">{comic.name}</td>
                <td className="email-cell">
                  <span
                    title={
                      comic.alternate_emails && comic.alternate_emails.length > 0
                        ? `Primary: ${comic.email}\nAlternate: ${comic.alternate_emails.join(', ')}`
                        : comic.email
                    }
                  >
                    {comic.email}
                    {comic.alternate_emails && comic.alternate_emails.length > 0 && (
                      <span className="alternate-count"> (+{comic.alternate_emails.length} more)</span>
                    )}
                  </span>
                </td>
                <td className="instagram-cell">
                  {comic.instagram_handle ? (
                    <a
                      href={`https://instagram.com/${comic.instagram_handle.replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="instagram-link"
                    >
                      @{comic.instagram_handle.replace('@', '')}
                    </a>
                  ) : (
                    <span className="no-instagram">-</span>
                  )}
                </td>
                <td>
                  <span className={`gender-badge ${comic.gender}`}>
                    {comic.gender}
                  </span>
                </td>
                <td>
                  <span className={`badge ${comic.friend ? 'yes' : 'no'}`}>
                    {comic.friend ? 'Yes' : 'No'}
                  </span>
                </td>
                <td>
                  <span className={`badge ${comic.is_cellar_comic ? 'yes' : 'no'}`}>
                    {comic.is_cellar_comic ? 'Yes' : 'No'}
                  </span>
                </td>
                <td>
                  <span className={`badge ${comic.can_host ? 'yes' : 'no'}`}>
                    {comic.can_host ? 'Yes' : 'No'}
                  </span>
                </td>
                <td>
                  <span className={`badge ${comic.do_not_book ? 'warning' : 'no'}`}>
                    {comic.do_not_book ? 'Yes' : 'No'}
                  </span>
                </td>
                <td>{formatSpotRate(comic)}</td>
                <td className="actions-cell">
                  <button
                    onClick={() => onEdit(comic)}
                    className="edit-button"
                    title="Edit comic"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => onDelete(comic.email)}
                    className="delete-button"
                    title="Delete comic"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ComicTable;
